import { MessageOutlined } from "@ant-design/icons";
import { List, useTable } from "@refinedev/antd";
import { App, Alert, Button, Modal, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import { api, type PrivateReplyStatus, type ProcessedComment, type Trigger } from "../../api/client";
import { renderTemplate } from "../../lib/template";

const STATUS_META: Record<PrivateReplyStatus, { color: string; label: string }> = {
  Pending: { color: "default", label: "В обработке" },
  Sent: { color: "green", label: "Отправлено" },
  Failed: { color: "red", label: "Ошибка" },
  SkippedAlreadySent: { color: "orange", label: "Уже отвечали" },
  NoTriggerMatched: { color: "blue", label: "Без ответа" },
};

/** Only these can still be answered — Meta allows one private reply per comment, ever. */
const ANSWERABLE: PrivateReplyStatus[] = ["NoTriggerMatched", "Failed"];

export const ActivityList = () => {
  const { tableProps, filters, setFilters, tableQuery } = useTable<ProcessedComment>({
    resource: "processed-comments",
    sorters: { initial: [{ field: "receivedAt", order: "desc" }] },
  });

  const statusFilter = filters?.find((f) => "field" in f && f.field === "status");

  const [replyTo, setReplyTo] = useState<ProcessedComment | null>(null);

  return (
    <List title="Журнал комментариев" canCreate={false}>
      <Typography.Paragraph type="secondary">
        Все комментарии под отслеживаемыми постами, включая те, что не подошли ни под один триггер —
        им можно ответить вручную заготовленным ответом.
      </Typography.Paragraph>

      <Space style={{ marginBottom: 16 }} wrap>
        {(Object.keys(STATUS_META) as PrivateReplyStatus[]).map((status) => {
          const active = statusFilter && "value" in statusFilter && statusFilter.value === status;
          return (
            <Tag.CheckableTag
              key={status}
              checked={Boolean(active)}
              onChange={(checked) =>
                setFilters(
                  checked ? [{ field: "status", operator: "eq", value: status }] : [],
                  "replace",
                )
              }
            >
              {STATUS_META[status].label}
            </Tag.CheckableTag>
          );
        })}
      </Space>

      <Table {...tableProps} rowKey="id">
        <Table.Column<ProcessedComment>
          title="Когда"
          dataIndex="receivedAt"
          render={(value: string) => new Date(value).toLocaleString("ru-RU")}
        />
        <Table.Column<ProcessedComment>
          title="От кого"
          dataIndex="fromUsername"
          render={(value: string | null) => (value ? `@${value}` : "—")}
        />
        <Table.Column<ProcessedComment>
          title="Комментарий"
          dataIndex="commentText"
          render={(value: string) => (
            <Typography.Text ellipsis style={{ maxWidth: 320 }}>
              {value}
            </Typography.Text>
          )}
        />
        <Table.Column<ProcessedComment>
          title="Сработало на"
          dataIndex="matchedKeyword"
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : <Typography.Text type="secondary">—</Typography.Text>)}
        />
        <Table.Column<ProcessedComment>
          title="Direct"
          dataIndex="privateReplyStatus"
          render={(value: PrivateReplyStatus, record) => {
            const meta = STATUS_META[value];
            const tag = <Tag color={meta.color}>{meta.label}</Tag>;

            // The error text is the whole reason this column exists — surface it, don't hide it.
            return record.errorMessage ? <Tooltip title={record.errorMessage}>{tag}</Tooltip> : tag;
          }}
        />
        <Table.Column<ProcessedComment>
          title=""
          dataIndex="actions"
          render={(_, record) =>
            ANSWERABLE.includes(record.privateReplyStatus) && record.trackedPostId ? (
              <Button size="small" icon={<MessageOutlined />} onClick={() => setReplyTo(record)}>
                Ответить
              </Button>
            ) : null
          }
        />
      </Table>

      <ReplyModal
        comment={replyTo}
        onClose={() => setReplyTo(null)}
        onSent={() => {
          setReplyTo(null);
          void tableQuery.refetch();
        }}
      />
    </List>
  );
};

const ReplyModal = ({
  comment,
  onClose,
  onSent,
}: {
  comment: ProcessedComment | null;
  onClose: () => void;
  onSent: () => void;
}) => {
  const { message } = App.useApp();
  const [trigger, setTrigger] = useState<Trigger | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const trackedPostId = comment?.trackedPostId ?? null;

  // Load the post's triggers when the modal opens, picking the same one the worker would have:
  // first enabled, by priority (the endpoint already returns them in that order). The backend
  // repeats this choice when sending, so the preview cannot lie about what goes out.
  useEffect(() => {
    if (!trackedPostId) {
      setTrigger(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void api
      .GET("/api/tracked-posts/{postId}/triggers", { params: { path: { postId: trackedPostId } } })
      .then(({ data }) => {
        if (cancelled) return;
        setTrigger(data?.find((t) => t.isEnabled) ?? null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [trackedPostId]);

  const send = async () => {
    if (!comment) return;
    setSending(true);

    const { error } = await api.POST("/api/processed-comments/{commentId}/reply", {
      params: { path: { commentId: comment.commentId } },
      body: { triggerRuleId: null },
    });

    setSending(false);

    if (error) {
      message.error("Не удалось отправить ответ");
      return;
    }

    message.success("Ответ отправлен");
    onSent();
  };

  return (
    <Modal
      open={Boolean(comment)}
      title="Ответить вручную"
      onCancel={onClose}
      onOk={() => void send()}
      okText="Отправить"
      cancelText="Отмена"
      confirmLoading={sending}
      okButtonProps={{ disabled: loading || !trigger }}
    >
      {comment && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            @{comment.fromUsername ?? "—"}: {comment.commentText}
          </Typography.Text>

          {loading ? (
            <Typography.Text type="secondary">Загружаю заготовку…</Typography.Text>
          ) : trigger ? (
            <>
              <Typography.Text>
                Ответ по триггеру <Typography.Text strong>{trigger.name}</Typography.Text>
              </Typography.Text>
              {trigger.publicReplyText && (
                <Alert type="info" message="Публично под комментарием" description={trigger.publicReplyText} />
              )}
              <Alert
                type="success"
                message="В Direct"
                description={
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                    {renderTemplate(
                      trigger.privateReplyMessage,
                      trigger.link,
                      comment.fromUsername ?? "",
                    )}
                  </pre>
                }
              />
            </>
          ) : (
            <Alert
              type="warning"
              message="У этого поста нет включённых триггеров"
              description="Добавьте триггер на странице задания — ответить нечем."
            />
          )}
        </Space>
      )}
    </Modal>
  );
};
